import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { authApi } from '../services/api';

interface User {
  id: number;
  email: string;
  phoneNumber: string | null;
  firstName: string | null;
  lastName: string | null;
  fullName: string | null;
  role: 'admin' | 'user';
  passwordResetRequired?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  passwordResetRequired: boolean;
  login: (email: string, password: string) => Promise<any>;
  completeOtpLogin: (email: string, otp: string) => Promise<any>;
  updateUserPasswordResetRequired: (value: boolean) => void;
  logout: () => void;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load token from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      // Verify token is still valid
      authApi.verifyToken(storedToken)
        .then((response) => {
          if (response.valid && response.user) {
            setUser({ ...response.user, passwordResetRequired: response.passwordResetRequired ?? response.user.passwordResetRequired });
          } else {
            // Token invalid, clear storage
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setToken(null);
            setUser(null);
          }
        })
        .catch(() => {
          // Token verification failed, clear storage
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = async (email: string, password: string) => {
    const response = await authApi.login(email, password);
    // If backend indicates OTP is required, store email and let caller handle navigation
    if (response.requiresOtp) {
      localStorage.setItem('pendingOtpEmail', email);
      return response;
    }

    setToken(response.token);
    const userWithReset = { ...response.user, passwordResetRequired: response.passwordResetRequired ?? response.user?.passwordResetRequired };
    setUser(userWithReset);
    localStorage.setItem('token', response.token);
    localStorage.setItem('user', JSON.stringify(userWithReset));

    // Default history flow to normal mode after successful login
    try {
      localStorage.setItem('historyEditMode', '1');
    } catch {
      // Ignore storage errors
    }

    // Mark this login session as eligible for a one-time draft popup
    try {
      localStorage.setItem('draftPromptEligible', '1');
      if (userWithReset.id != null) {
        localStorage.setItem('draftPromptUserId', String(userWithReset.id));
      }
    } catch {
      // Ignore storage errors
    }

    return response;
  };

  const completeOtpLogin = async (email: string, otp: string) => {
    const response = await authApi.verifyOtp(email, otp);
    setToken(response.token);
    const userWithReset = { ...response.user, passwordResetRequired: response.passwordResetRequired ?? response.user?.passwordResetRequired };
    setUser(userWithReset);
    localStorage.setItem('token', response.token);
    localStorage.setItem('user', JSON.stringify(userWithReset));
    localStorage.removeItem('pendingOtpEmail');

    // Default history flow to normal mode after successful OTP login
    try {
      localStorage.setItem('historyEditMode', '1');
    } catch {
      // Ignore storage errors
    }

    // Mark this login session as eligible for a one-time draft popup
    try {
      localStorage.setItem('draftPromptEligible', '1');
      if (userWithReset.id != null) {
        localStorage.setItem('draftPromptUserId', String(userWithReset.id));
      }
    } catch {
      // Ignore storage errors
    }

    return response;
  };

  const updateUserPasswordResetRequired = (value: boolean) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, passwordResetRequired: value };
      localStorage.setItem('user', JSON.stringify(updated));
      return updated;
    });
  };


  const logout = () => {
    const tokenSnapshot = token ?? localStorage.getItem('token');
    setToken(null);
    setUser(null);

    // Call backend logout with the token we had (even if storage is cleared immediately)
    if (tokenSnapshot) {
      authApi.logout(tokenSnapshot).then(() => {}).catch(() => {});
    }

    // Clear all app-related localStorage data on logout
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('pendingOtpEmail');
      localStorage.removeItem('draftPromptEligible');
      localStorage.removeItem('draftPromptUserId');
      localStorage.removeItem('draftPatientDetailsId');
      localStorage.removeItem('historyEditMode');
    } catch {
      // Ignore storage errors
    }
  };

  const value: AuthContextType = {
    user,
    token,
    passwordResetRequired: user?.passwordResetRequired === true,
    login,
    completeOtpLogin,
    updateUserPasswordResetRequired,
    logout,
    isLoading,
    isAuthenticated: !!token && !!user,
    isAdmin: user?.role === 'admin',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

